import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin, Alert, Button, Card } from 'antd';
import { ReloadOutlined, LoginOutlined } from '@ant-design/icons';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { refreshAccessToken, initializeAuth, fetchCurrentUser } from '../store/slices/authSlice';
import { ErrorBoundary } from './ErrorBoundary';
import { canAccessMenu, isSystemAdministrator } from '../utils/menuPermissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: (1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10)[]; // Optional role-based access
  requiredMenu?: string | string[]; // Optional menu-permission access
  requireSuperuser?: boolean; // Require is_superuser=true
  allowSystemAdministrator?: boolean; // Also let the System Administrator role (role 1) satisfy requireSuperuser. Opt-in per route — do not default this on, it would silently widen every other requireSuperuser route.
  fallbackLoading?: React.ReactNode; // Custom loading component
  fallbackError?: React.ReactNode; // Custom error component
}



// Enhanced loading component
const LoadingFallback: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    flexDirection: 'column'
  }}>
    <Spin size="large" />
    <p style={{ marginTop: 16, color: '#666' }}>{message}</p>
  </div>
);

// Enhanced error component
const ErrorFallback: React.FC<{
  error?: string;
  onRetry?: () => void;
  onLogin?: () => void;
  showLoginButton?: boolean;
}> = ({ error, onRetry, onLogin, showLoginButton = false }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    padding: 20
  }}>
    <Card style={{ maxWidth: 500, textAlign: 'center' }}>
      <Alert
        message="Authentication Error"
        description={error || 'An error occurred while checking authentication.'}
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        {onRetry && (
          <Button icon={<ReloadOutlined />} onClick={onRetry}>
            Retry
          </Button>
        )}
        {showLoginButton && onLogin && (
          <Button type="primary" icon={<LoginOutlined />} onClick={onLogin}>
            Go to Login
          </Button>
        )}
      </div>
    </Card>
  </div>
);

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles,
  requiredMenu,
  requireSuperuser,
  allowSystemAdministrator,
  fallbackLoading,
  fallbackError
}) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [retryCount, setRetryCount] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [menuRefreshAttempted, setMenuRefreshAttempted] = useState(false);

  const authState = useAppSelector((state) => state.auth);

  const { isAuthenticated, user, token, loading, initialized, error } = authState;
  const menuAccessDenied = !!requiredMenu && initialized && isAuthenticated && !!token && !canAccessMenu(user, requiredMenu);

  useEffect(() => {
    // Reset error state when location changes
    setHasError(false);
    setMenuRefreshAttempted(false);
  }, [location.pathname]);

  useEffect(() => {
    // Try to refresh token if we have a refresh token but no access token
    if (!token && localStorage.getItem('refresh_token') && !loading && initialized) {
      dispatch(refreshAccessToken()).catch((err) => {
        setHasError(true);
      });
    }
  }, [token, dispatch, loading, initialized]);

  useEffect(() => {
    if (menuAccessDenied && !menuRefreshAttempted) {
      setMenuRefreshAttempted(true);
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, menuAccessDenied, menuRefreshAttempted]);

  // Handle retry functionality
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setHasError(false);

    if (!initialized) {
      dispatch(initializeAuth());
    } else if (!token && localStorage.getItem('refresh_token')) {
      dispatch(refreshAccessToken());
    }
  };

  // Handle navigation to login
  const handleGoToLogin = () => {
    window.location.href = '/login';
  };

  // Show error state if there's an authentication error (but not normal initialization errors)
  const shouldShowError = hasError || (
    error &&
    initialized &&
    !error.includes('No stored authentication tokens found') &&
    !error.includes('initialization failed')
  );

  if (shouldShowError) {
    return fallbackError || (
      <ErrorFallback
        error={error || 'Authentication failed'}
        onRetry={handleRetry}
        onLogin={handleGoToLogin}
        showLoginButton={true}
      />
    );
  }

  // Show loading spinner while checking authentication or initializing
  if (loading || !initialized) {
    const loadingMessage = loading
      ? 'Authenticating...'
      : 'Initializing application...';

    return fallbackLoading || <LoadingFallback message={loadingMessage} />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

	// Check role-based access if required roles are specified
	if (requiredRoles && requiredRoles.length > 0 && user) {
		const userRoles = user.roles?.length ? user.roles : [user.role];
		if (!requiredRoles.some((role) => userRoles.includes(role))) {
			return (
				<div style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '100vh',
					flexDirection: 'column'
				}}>
					<Card style={{ textAlign: 'center', maxWidth: 400 }}>
						<Alert
							message="Access Denied"
							description={`You don't have permission to access this page. Required roles: ${requiredRoles.join(', ')}`}
							type="warning"
							showIcon
							style={{ marginBottom: 16 }}
						/>
						<Button onClick={() => window.history.back()}>
							Go Back
						</Button>
					</Card>
				</div>
			);
		}
	}

	// Check superuser requirement (runs independently of requiredRoles).
	// allowSystemAdministrator is an explicit per-route opt-in so that
	// requireSuperuser stays strictly is_superuser-only everywhere else.
	if (
		requireSuperuser
		&& user
		&& !user.is_superuser
		&& !(allowSystemAdministrator && isSystemAdministrator(user))
	) {
		return (
			<div style={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				height: '100vh',
				flexDirection: 'column'
			}}>
				<Card style={{ textAlign: 'center', maxWidth: 400 }}>
					<Alert
						message="Access Denied"
						description="This page is restricted to super administrators only."
						type="warning"
						showIcon
						style={{ marginBottom: 16 }}
					/>
					<Button onClick={() => window.history.back()}>
						Go Back
					</Button>
				</Card>
			</div>
		);
	}

  if (requiredMenu && !canAccessMenu(user, requiredMenu)) {
    if (!menuRefreshAttempted) {
      return fallbackLoading || <LoadingFallback message="Refreshing permissions..." />;
    }

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <Card style={{ textAlign: 'center', maxWidth: 400 }}>
          <Alert
            message="Access Denied"
            description="You don't have menu permission to access this page."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Button onClick={() => window.history.back()}>
            Go Back
          </Button>
        </Card>
      </div>
    );
  }

  // Wrap children in error boundary
  return (
    <ErrorBoundary>
      {children}
    </ErrorBoundary>
  );
};
